import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Loader2, Search } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const fmt = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

const STATUS_STYLE = {
  active: "bg-sage text-forest",
  invited: "bg-gold/15 text-gold-dark",
  deactivated: "bg-charcoal/10 text-taupe",
};

export default function AdminMembers() {
  const { member: me } = useAuth();
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [status, setStatus] = useState("");
  const [completed, setCompleted] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const canExport = (me?.permissions || []).some((p) => p === "*" || p === "export_directory");

  const load = (over = {}) => {
    const p = {
      search, filter, status, completed, registered_from: from, registered_to: to, ...over,
    };
    const qs = new URLSearchParams(
      Object.entries(p).filter(([, v]) => v !== "" && v != null)
    ).toString();
    api
      .get(`/admin/members?${qs}`)
      .then(({ data }) => setData(data))
      .catch(() => setData({ members: [], count: 0 }));
  };

  useEffect(() => {
    document.title = "Members | Rooted Healing Admin";
    load({ search: "", filter: "all" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="admin-members">
      <h1 className="font-serif text-3xl text-forest">Members</h1>
      <p className="mt-2 text-sm text-taupe">
        Searchable member directory. Opening a member record is audit-logged, and sensitive
        wellness records require the appropriate permission.
      </p>
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-56">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-taupe" />
          <input
            data-testid="members-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search by name or email"
            className="w-full rounded-full border border-forest/20 bg-white/70 py-3 pl-11 pr-5 text-sm outline-none focus:border-gold"
          />
        </div>
        <select
          data-testid="members-filter"
          value={filter}
          onChange={(e) => {
            setFilter(e.target.value);
            load({ filter: e.target.value });
          }}
          className="rounded-full border border-forest/20 bg-white/70 px-5 py-3 text-sm outline-none focus:border-gold"
        >
          <option value="all">All members</option>
          <option value="newsletter">Newsletter consented</option>
          <option value="reminders">Reminders consented</option>
        </select>
        <select
          data-testid="members-status-filter"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            load({ status: e.target.value });
          }}
          className="rounded-full border border-forest/20 bg-white/70 px-5 py-3 text-sm outline-none focus:border-gold"
        >
          <option value="">Any status</option>
          <option value="active">Active</option>
          <option value="deactivated">Deactivated</option>
        </select>
        <select
          data-testid="members-completed-filter"
          value={completed}
          onChange={(e) => {
            setCompleted(e.target.value);
            load({ completed: e.target.value });
          }}
          className="rounded-full border border-forest/20 bg-white/70 px-5 py-3 text-sm outline-none focus:border-gold"
        >
          <option value="">Any discoveries</option>
          <option value="yes">Completed a discovery</option>
          <option value="no">No completed discovery</option>
        </select>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-xs text-taupe">
          Registered from
          <input
            data-testid="members-from"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="rounded-full border border-forest/20 bg-white/70 px-4 py-2 text-xs text-charcoal outline-none focus:border-gold"
          />
        </label>
        <label className="flex items-center gap-2 text-xs text-taupe">
          to
          <input
            data-testid="members-to"
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="rounded-full border border-forest/20 bg-white/70 px-4 py-2 text-xs text-charcoal outline-none focus:border-gold"
          />
        </label>
        <button
          data-testid="members-search-button"
          onClick={() => load()}
          className="rounded-full bg-forest px-6 py-2.5 text-sm font-semibold text-ivory hover:bg-forest-deep"
        >
          Apply
        </button>
        {canExport && (
          <a
            data-testid="members-export-all"
            href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/export/directory.csv?fields=name,email,registered_at,status,newsletter_consent,reminders_consent,assessment_count&registered_from=${from}&registered_to=${to}&newsletter=${filter === "newsletter" ? "true" : ""}&completed=${completed}&status=${status}`}
            className="inline-flex items-center gap-2 rounded-full border border-forest/25 px-5 py-2.5 text-sm font-semibold text-forest hover:border-gold"
          >
            <Download className="h-4 w-4" />
            Export directory CSV
          </a>
        )}
      </div>

      {!data ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-7 w-7 animate-spin text-forest" />
        </div>
      ) : (
        <div className="mt-6 overflow-x-auto rounded-[1.75rem] border border-forest/10 bg-white/75">
          <table className="w-full min-w-[860px] text-left text-sm">
            <thead>
              <tr className="border-b border-forest/10 text-xs uppercase tracking-[0.15em] text-taupe">
                <th className="px-5 py-4">Name</th>
                <th className="px-5 py-4">Email</th>
                <th className="px-5 py-4">Status</th>
                <th className="px-5 py-4">Registered</th>
                <th className="px-5 py-4">Last sign-in</th>
                <th className="px-5 py-4">Discoveries</th>
                <th className="px-5 py-4">Last discovery</th>
                <th className="px-5 py-4">Newsletter</th>
                <th className="px-5 py-4">Reminders</th>
              </tr>
            </thead>
            <tbody>
              {data.members.map((m) => (
                <tr
                  key={m.user_id}
                  data-testid={`member-row-${m.user_id}`}
                  className="border-b border-forest/5 transition-colors hover:bg-sage/40"
                >
                  <td className="px-5 py-3.5">
                    <Link
                      to={`/admin/members/${m.user_id}`}
                      data-testid={`member-link-${m.user_id}`}
                      className="font-semibold text-forest underline-offset-4 hover:underline"
                    >
                      {m.name}
                    </Link>
                  </td>
                  <td className="px-5 py-3.5 text-charcoal/75">{m.email}</td>
                  <td className="px-5 py-3.5">
                    <span
                      className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${STATUS_STYLE[m.status] || STATUS_STYLE.active}`}
                    >
                      {m.status || "active"}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-charcoal/75">{fmt(m.created_at)}</td>
                  <td className="px-5 py-3.5 text-charcoal/75">{fmt(m.last_login_at)}</td>
                  <td className="px-5 py-3.5 text-charcoal/75">{m.assessment_count}</td>
                  <td className="px-5 py-3.5 text-charcoal/75">{fmt(m.last_assessment_at)}</td>
                  <td className="px-5 py-3.5">{m.consents?.newsletter ? "Yes" : "No"}</td>
                  <td className="px-5 py-3.5">{m.consents?.reminders ? "Yes" : "No"}</td>
                </tr>
              ))}
              {data.members.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-5 py-10 text-center text-taupe">
                    No members match this search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
